import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { getPlatformAssetUrl } from '../../utils/api';
import { prepareFileForUpload } from '../../utils/imageUploadCompress';
import { useAuth } from '../../context/AuthContext';
import { canManageCrmEmployees } from '../../utils/crmPermissions';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Mail, Phone, MapPin, User, Camera, IndianRupee, Edit,
} from 'lucide-react';
import CrmCharts from './CrmCharts';
import CrmActivityTimeline from './CrmActivityTimeline';
import CrmEmployeeAttendanceChart from './CrmEmployeeAttendanceChart';
import CrmEmployeeEditDialog from './CrmEmployeeEditDialog';
import CrmEmployeeMailSettings from './CrmEmployeeMailSettings';
import { formatDate, formatDateTime } from '../../utils/appDateTime';

function formatCurrency(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return `₹${num.toLocaleString('en-IN')}`;
}

function formatSalaryType(type) {
  if (type === 'fixed') return 'Fixed';
  if (type === 'fixed_plus_incentive') return 'Fixed + Incentive';
  return type || '—';
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-foreground text-right">{value}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const active = status === 'active';
  return (
    <Badge variant={active ? 'default' : 'secondary'} className="text-xs capitalize">
      {status || 'unknown'}
    </Badge>
  );
}

function formatTerritories(territories) {
  if (!territories?.length) return '—';
  return territories.map((t) => {
    const parts = [t.state, t.district, t.city].filter(Boolean);
    return parts.join(', ');
  }).join(' · ');
}

export default function CrmEmployeeProfile() {
  const { id } = useParams();
  const { user, patchUser } = useAuth();
  const { toast } = useToast();
  const photoInputRef = useRef(null);
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoCacheKey, setPhotoCacheKey] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const canManage = canManageCrmEmployees(user);
  const isSuperAdmin = canManage;

  const loadProfile = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [aRes, actRes] = await Promise.all([
        api.get(`/crm/employees/${id}/analytics`),
        api.get(`/crm/employees/${id}/activities`),
      ]);
      setData(aRes.data?.data || null);
      setActivities(actRes.data?.data?.items || []);
    } catch {
      setData(null);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) return <p className="p-6">Loading…</p>;
  if (!data?.employee) return <p className="p-6">Employee not found</p>;

  const emp = data.employee;
  const isOwnProfile = Number(emp.user_id) === Number(user?.id);
  const canEditPhoto = isSuperAdmin || isOwnProfile;
  const conv = data.conversion || {};
  const fullName = [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.email || 'Employee';
  const initials = ((emp.first_name?.[0] || '') + (emp.last_name?.[0] || '')).toUpperCase()
    || (emp.email?.[0]?.toUpperCase() || '?');
  const avatarUrl = getPlatformAssetUrl(emp.avatar, photoCacheKey);

  const handlePhotoClick = () => {
    if (!canEditPhoto || uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image (JPG, PNG, GIF, WebP).', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('photo', prepared);
      const res = await api.post(`/crm/employees/${id}/photo`, formData);
      const newAvatar = res.data?.data?.avatar;
      if (newAvatar) {
        setPhotoCacheKey(String(Date.now()));
        setData((prev) => (
          prev ? { ...prev, employee: { ...prev.employee, avatar: newAvatar } } : null
        ));
        if (isOwnProfile) {
          patchUser({ avatar: newAvatar });
        }
        toast({ title: 'Photo updated', description: 'Profile photo has been saved.' });
      }
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not upload photo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/crm/employees" className="text-sm text-blue-600 hover:underline">← Back to employees</Link>
      </div>

      <Card className="border-border overflow-hidden">
        <div className="bg-gradient-to-r from-blue-800/80 to-card h-24" />
        <CardContent className="relative px-6 pb-6">
          <div className="flex flex-col sm:flex-row gap-6 -mt-14">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handlePhotoChange}
            />
            {canEditPhoto ? (
              <button
                type="button"
                onClick={handlePhotoClick}
                disabled={uploadingPhoto}
                className="relative h-28 w-28 shrink-0 rounded-xl border-4 border-border shadow-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-background hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Avatar className="h-full w-full rounded-xl">
                  <AvatarImage src={avatarUrl} alt={fullName} className="object-cover" />
                  <AvatarFallback className="rounded-xl bg-blue-600/80 text-xl text-white">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-xl">
                  <Camera className="w-8 h-8 text-white" />
                </span>
                {uploadingPhoto && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
                    <span className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                  </span>
                )}
              </button>
            ) : (
              <Avatar className="h-28 w-28 shrink-0 rounded-xl border-4 border-border shadow-xl">
                <AvatarImage src={avatarUrl} alt={fullName} className="object-cover rounded-xl" />
                <AvatarFallback className="rounded-xl bg-blue-600/80 text-xl text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
            )}
            <div className="flex-1 pt-2 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-bold">{fullName}</h1>
                  <p className="text-muted-foreground">{emp.designation || '—'}</p>
                </div>
                {canManage && (
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{emp.employee_code}</Badge>
                {emp.role_name && <Badge variant="outline">{emp.role_name}</Badge>}
                <StatusBadge status={emp.status || emp.user_status} />
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 text-sm">
                {emp.email && (
                  <a href={`mailto:${emp.email}`} className="inline-flex items-center gap-2 text-foreground hover:text-blue-600">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {emp.email}
                  </a>
                )}
                {emp.phone && (
                  <a href={`tel:${emp.phone}`} className="inline-flex items-center gap-2 text-foreground hover:text-blue-600">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    {emp.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="w-5 h-5" /> Profile Information
              </CardTitle>
              {canManage && (
                <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)} aria-label="Edit profile">
                  <Edit className="w-4 h-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <DetailRow label="Employee code" value={emp.employee_code || '—'} />
            <DetailRow label="Designation" value={emp.designation || '—'} />
            <DetailRow label="Role" value={emp.role_name || emp.role_slug || '—'} />
            <DetailRow label="Joining date" value={formatDate(emp.joining_date)} />
            <DetailRow label="Reports to" value={emp.reports_to_name || '—'} />
            <DetailRow label="Last login" value={formatDateTime(emp.last_login)} />
            <DetailRow label="Territories" value={formatTerritories(emp.territories)} />
            {emp.address && (
              <div className="flex gap-2 pt-2 border-t mt-2">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <span className="text-foreground">{emp.address}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {isSuperAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <IndianRupee className="w-5 h-5" /> Compensation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <DetailRow label="Salary type" value={formatSalaryType(emp.salary_type)} />
              <DetailRow label="Base salary" value={formatCurrency(emp.base_salary)} />
              <DetailRow label="Incentive per join" value={formatCurrency(emp.incentive_per_join)} />
              <DetailRow label="Monthly target" value={emp.monthly_target ?? '—'} />
            </CardContent>
          </Card>
        )}
      </div>

      {isSuperAdmin && (
        <CrmEmployeeMailSettings employeeId={id} employeeName={fullName} />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['today', 'weekly', 'monthly', 'all_time'].map((p) => (
          <Card key={p}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase">{p.replace('_', ' ')} inquiries</p>
              <p className="text-2xl font-bold">{data.inquiry_stats?.[p] ?? 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Conversion Rate</CardTitle></CardHeader>
        <CardContent>
          <p>Total Inquiries: <strong>{conv.total_inquiries}</strong></p>
          <p>Schools Joined: <strong>{conv.schools_joined}</strong></p>
          <p>Conversion Rate: <strong>{conv.conversion_rate}%</strong></p>
        </CardContent>
      </Card>

      <CrmEmployeeAttendanceChart employeeId={id} />

      <CrmCharts charts={data.charts} />
      <CrmActivityTimeline items={activities} />

      {canManage && (
        <CrmEmployeeEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          employee={emp}
          onSaved={() => loadProfile(false)}
        />
      )}
    </div>
  );
}
